import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { subHours } from 'date-fns';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { FileDown, FileText, FileType, FileSpreadsheet, FileCode, Loader2, Image } from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
  AreaChart,
  Area,
} from 'recharts';
import { useGetAnalyticsTimeSeriesQuery, useLazyGetExportSensorTimeSeriesQuery } from '@/services';
import { Skeleton } from '@/components/ui/skeleton';
import { GaugeChart } from '@/components/charts/gauge-chart';
import { Brain, Sparkles, TrendingUp, AlertCircle, CheckCircle, AlertTriangle } from 'lucide-react';
import { aiService } from '@/services/ai-service';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface AnalyticsTabProps {
  deviceId: string;
  timeRange: string;
  setTimeRange: (range: string) => void;
  timeRangeOptions: { value: string; label: string }[];
}

interface TimeSeriesPoint {
  timestamp: string;
  value?: number | null;
}

interface TimeSeries {
  sensor_id: string;
  sensor_name: string;
  sensor_type: string;
  unit?: string | null;
  series: TimeSeriesPoint[];
}

const CHART_COLORS = [
  '#2563eb', // Blue
  '#16a34a', // Green
  '#dc2626', // Red
  '#ca8a04', // Yellow
  '#9333ea', // Purple
  '#0891b2', // Cyan
];

const AGGREGATION_OPTIONS = [
  { value: 'avg', label: 'Average' },
  { value: 'min', label: 'Minimum' },
  { value: 'max', label: 'Maximum' },
  { value: 'sum', label: 'Sum' },
  { value: 'count', label: 'Count' },
];

const INTERVAL_OPTIONS = [
  { value: 'hour', label: 'Hourly' },
  { value: 'day', label: 'Daily' },
  { value: 'week', label: 'Weekly' },
  { value: 'month', label: 'Monthly' },
];

const CHART_TYPES = [
  { value: 'line', label: 'Line' },
  { value: 'area', label: 'Area' },
  { value: 'gauge', label: 'Gauge' },
];

// --- Tooltip Component ---
function ChartTooltip({ filteredSeries }: { filteredSeries: TimeSeries[] }) {
  return (
    <RechartsTooltip
      contentStyle={{
        background: 'var(--background)',
        border: '1px solid var(--border)',
        borderRadius: 8,
        boxShadow: '0 2px 8px 0 rgba(0,0,0,0.08)',
        color: 'var(--foreground)',
        fontSize: 14,
        padding: 12,
      }}
      labelStyle={{
        color: 'var(--muted-foreground)',
        fontWeight: 500,
        marginBottom: 4,
      }}
      itemStyle={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        margin: 0,
        padding: 0,
      }}
      formatter={(value: unknown, name: string) => {
        const unit = filteredSeries.find((s) => s.sensor_name === name)?.unit || '';
        const formatted = typeof value === 'number' ? (value % 1 === 0 ? value : value.toFixed(2)) : value;
        return [`${formatted}${unit ? ` ${unit}` : ''}`, name];
      }}
      labelFormatter={(timestamp: string) =>
        timestamp
          ? new Date(timestamp).toLocaleString(undefined, {
              year: 'numeric',
              month: 'short',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
            })
          : ''
      }
    />
  );
}

// --- Chart Controls Component ---
function ChartControls({
  selectedSensorType,
  setSelectedSensorType,
  sensorTypes,
  interval,
  setInterval,
  aggregation,
  setAggregation,
  chartType,
  setChartType,
  timeRange,
  setTimeRange,
  timeRangeOptions,
  deviceId,
}: {
  selectedSensorType: string;
  setSelectedSensorType: (v: string) => void;
  sensorTypes: string[];
  interval: string;
  setInterval: (v: string) => void;
  aggregation: string;
  setAggregation: (v: string) => void;
  chartType: 'line' | 'area' | 'gauge';
  setChartType: (v: 'line' | 'area' | 'gauge') => void;
  timeRange: string;
  setTimeRange: (v: string) => void;
  timeRangeOptions: { value: string; label: string }[];
  deviceId: string;
}) {
  const [triggerExport] = useLazyGetExportSensorTimeSeriesQuery();
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async (format: 'json' | 'csv' | 'pdf' | 'xlsx' | 'chart') => {
    const startDate = subHours(new Date(), parseInt(timeRange)).toISOString();
    const endDate = new Date().toISOString();

    const filename = `sensor-data-${startDate.replace(/:/g, '-')}TO${endDate.replace(/:/g, '-')}.${format}`;
    try {
      setIsExporting(true);

      const result = await triggerExport({
        deviceIds: deviceId,
        sensorTypes: selectedSensorType !== 'all' ? selectedSensorType : undefined,
        startDate: new Date(Date.now() - parseInt(timeRange) * 60 * 60 * 1000).toISOString(),
        endDate: new Date().toISOString(),
        interval: interval as 'hour' | 'day' | 'week' | 'month',
        aggregation: aggregation as 'avg' | 'min' | 'max' | 'sum' | 'count',
        format,
        chartType: format === 'chart' ? chartType : undefined,
      }).unwrap();

      const mimeTypes: Record<string, string> = {
        csv: 'text/csv',
        json: 'application/json',
        xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        pdf: 'application/pdf',
      };

      const blob = new Blob([result], { type: mimeTypes[format] });
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();

      URL.revokeObjectURL(url);
    } catch (err) {
      // Handle export error
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="flex flex-wrap gap-4 justify-between items-center">
      <div className="flex flex-wrap gap-2 items-center">
        <Select value={selectedSensorType} onValueChange={setSelectedSensorType}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Sensor Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sensor Types</SelectItem>
            {sensorTypes.map((type) => (
              <SelectItem key={type} value={type}>
                {type}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={interval} onValueChange={setInterval}>
          <SelectTrigger className="w-[130px]">
            <SelectValue placeholder="Interval" />
          </SelectTrigger>
          <SelectContent>
            {INTERVAL_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={aggregation} onValueChange={setAggregation}>
          <SelectTrigger className="w-[130px]">
            <SelectValue placeholder="Aggregation" />
          </SelectTrigger>
          <SelectContent>
            {AGGREGATION_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Tabs value={chartType} onValueChange={(value: string) => setChartType(value as 'line' | 'area' | 'gauge')}>
          <TabsList>
            {CHART_TYPES.map((type) => (
              <TabsTrigger key={type.value} value={type.value}>
                {type.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        <Select value={timeRange} onValueChange={setTimeRange}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Time Range" />
          </SelectTrigger>
          <SelectContent>
            {timeRangeOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="w-[190px]" disabled={isExporting}>
              {isExporting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <FileDown className="mr-2 h-4 w-4" />
                  Export
                </>
              )}
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuContent className="w-[140px]" align="end">
            <DropdownMenuItem onClick={() => handleExport('pdf')} disabled={isExporting}>
              <FileType className="mr-2 h-4 w-4" />
              pdf
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleExport('csv')} disabled={isExporting}>
              <FileText className="mr-2 h-4 w-4" />
              csv
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleExport('xlsx')} disabled={isExporting}>
              <FileSpreadsheet className="mr-2 h-4 w-4" />
              excel
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleExport('json')} disabled={isExporting}>
              <FileCode className="mr-2 h-4 w-4" />
              json
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleExport('chart')} disabled={isExporting}>
              <Image className="mr-2 h-4 w-4" />
              chart
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

// --- Gauge Chart Grid Component ---
function GaugeChartGrid({ series }: { series: TimeSeries[] }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 py-4">
      {series.map((sensorSeries) => {
        const latestValue =
          sensorSeries.series && sensorSeries.series.length > 0
            ? sensorSeries.series[sensorSeries.series.length - 1].value
            : 0;

        const values = (sensorSeries.series || [])
          .map((point) => point.value)
          .filter((val): val is number => val !== null && val !== undefined);

        const minValue = values.length > 0 ? Math.min(...values) : 0;
        const maxValue = values.length > 0 ? Math.max(...values) : 100;

        return (
          <div key={sensorSeries.sensor_id} className="flex flex-col items-center">
            <GaugeChart
              value={latestValue ?? 0}
              min={minValue}
              max={maxValue * 1.2}
              unit={sensorSeries.unit ?? undefined}
              label={sensorSeries.sensor_name}
              threshold={{
                warning: 0.7,
                critical: 0.9,
              }}
            />
          </div>
        );
      })}
    </div>
  );
}

// --- Grouped Charts Component ---
function GroupedCharts({
  chartType,
  sensorsByUnit,
  filteredSeries,
  interval,
}: {
  chartType: string;
  sensorsByUnit: Record<string, TimeSeries[]>;
  filteredSeries: TimeSeries[];
  interval: string;
}) {
  return (
    <div className="space-y-8">
      {Object.entries(sensorsByUnit).map(([unit, unitSeries]) => {
        const allTimestamps = new Set<string>();
        unitSeries.forEach((series) => {
          if (series.series) {
            series.series.forEach((point) => {
              if (point && point.timestamp) allTimestamps.add(point.timestamp);
            });
          }
        });

        const chartData: Record<string, unknown>[] = [];
        Array.from(allTimestamps)
          .sort()
          .forEach((timestamp) => {
            const dataPoint: Record<string, unknown> = { timestamp };
            unitSeries.forEach((series) => {
              if (series.series) {
                const point = series.series.find((p) => p && p.timestamp === timestamp);
                dataPoint[series.sensor_name] = point?.value !== undefined ? point.value : null;
              }
            });
            chartData.push(dataPoint);
          });

        return (
          <div key={unit} className="h-[300px]">
            <h3 className="text-sm font-medium mb-2">{unit === 'unknown' ? 'Unknown Unit' : unit}</h3>
            <ResponsiveContainer width="100%" height="100%">
              {chartType === 'line' ? (
                <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 25 }}>
                  <XAxis
                    dataKey="timestamp"
                    tickFormatter={(timestamp: string) => {
                      const date = new Date(timestamp);
                      return interval === 'hour'
                        ? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                        : date.toLocaleDateString([], { month: 'short', day: 'numeric' });
                    }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis />
                  <RechartsTooltip
                    contentStyle={{
                      background: 'var(--background)',
                      border: '1px solid var(--border)',
                      borderRadius: 8,
                      boxShadow: '0 2px 8px 0 rgba(0,0,0,0.08)',
                      color: 'var(--foreground)',
                      fontSize: 14,
                      padding: 12,
                    }}
                    labelStyle={{
                      color: 'var(--muted-foreground)',
                      fontWeight: 500,
                      marginBottom: 4,
                    }}
                    itemStyle={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      margin: 0,
                      padding: 0,
                    }}
                    formatter={(value: unknown, name: string) => {
                      const unit = filteredSeries.find((s) => s.sensor_name === name)?.unit || '';
                      const formatted =
                        typeof value === 'number' ? (value % 1 === 0 ? value : value.toFixed(2)) : value;
                      return [`${formatted}${unit ? ` ${unit}` : ''}`, name];
                    }}
                    labelFormatter={(timestamp: string) =>
                      timestamp
                        ? new Date(timestamp).toLocaleString(undefined, {
                            year: 'numeric',
                            month: 'short',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        : ''
                    }
                  />
                  <Legend />
                  {unitSeries.map((series, index) => {
                    const color = CHART_COLORS[index % CHART_COLORS.length];
                    return (
                      <Line
                        key={series.sensor_id}
                        type="monotone"
                        dataKey={series.sensor_name}
                        name={`${series.sensor_name} (${series.unit || ''})`}
                        stroke={color}
                        strokeWidth={2}
                        dot={{ r: 2 }}
                        activeDot={{ r: 4 }}
                        connectNulls
                      />
                    );
                  })}
                </LineChart>
              ) : (
                <AreaChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 25 }}>
                  <XAxis
                    dataKey="timestamp"
                    tickFormatter={(timestamp: string) => {
                      const date = new Date(timestamp);
                      return interval === 'hour'
                        ? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                        : date.toLocaleDateString([], { month: 'short', day: 'numeric' });
                    }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis />
                  <RechartsTooltip
                    contentStyle={{
                      background: 'var(--background)',
                      border: '1px solid var(--border)',
                      borderRadius: 8,
                      boxShadow: '0 2px 8px 0 rgba(0,0,0,0.08)',
                      color: 'var(--foreground)',
                      fontSize: 14,
                      padding: 12,
                    }}
                    labelStyle={{
                      color: 'var(--muted-foreground)',
                      fontWeight: 500,
                      marginBottom: 4,
                    }}
                    itemStyle={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      margin: 0,
                      padding: 0,
                    }}
                    formatter={(value: unknown, name: string) => {
                      const unit = filteredSeries.find((s) => s.sensor_name === name)?.unit || '';
                      const formatted =
                        typeof value === 'number' ? (value % 1 === 0 ? value : value.toFixed(2)) : value;
                      return [`${formatted}${unit ? ` ${unit}` : ''}`, name];
                    }}
                    labelFormatter={(timestamp: string) =>
                      timestamp
                        ? new Date(timestamp).toLocaleString(undefined, {
                            year: 'numeric',
                            month: 'short',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        : ''
                    }
                  />
                  <Legend />
                  {unitSeries.map((series, index) => {
                    const color = CHART_COLORS[index % CHART_COLORS.length];
                    return (
                      <Area
                        key={series.sensor_id}
                        type="monotone"
                        dataKey={series.sensor_name}
                        name={`${series.sensor_name} (${series.unit || ''})`}
                        stroke={color}
                        fill={`${color}33`}
                        strokeWidth={2}
                        activeDot={{ r: 4 }}
                        connectNulls
                      />
                    );
                  })}
                </AreaChart>
              )}
            </ResponsiveContainer>
          </div>
        );
      })}
    </div>
  );
}

// --- Sensor Card Component ---
function SensorCard({
  series,
  color,
  chartType,
  interval,
  isLoading,
}: {
  series: TimeSeries;
  color: string;
  chartType: string;
  interval: string;
  isLoading: boolean;
}) {
  const sensorData = useMemo(
    () =>
      (series.series || []).map((point) => ({
        timestamp: point.timestamp,
        value: point.value,
      })),
    [series],
  );

  const stats = useMemo(() => {
    const values = sensorData.map((d) => d.value).filter((v): v is number => v !== null && v !== undefined);
    return {
      minValue: values.length > 0 ? Math.min(...values) : null,
      maxValue: values.length > 0 ? Math.max(...values) : null,
      avgValue: values.length > 0 ? values.reduce((sum, v) => sum + v, 0) / values.length : null,
      latestValue: values.length > 0 ? values[values.length - 1] : null,
    };
  }, [sensorData]);

  return (
    <Card key={series.sensor_id}>
      <CardHeader className="pb-2 flex flex-row justify-between items-center w-full">
        <div className="flex items-center">
          <CardTitle className="text-lg">
            {series.sensor_name}
            <span className="text-sm text-muted-foreground ml-2">({series.sensor_type})</span>
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-[200px] w-full rounded-md" />
        ) : (
          <>
            <div className="h-[200px]">
              {chartType === 'gauge' ? (
                <div className="flex items-center justify-center h-[200px]">
                  <GaugeChart
                    value={stats.latestValue ?? 0}
                    min={stats.minValue !== null ? stats.minValue : 0}
                    max={stats.maxValue !== null ? stats.maxValue * 1.2 : 100}
                    unit={series.unit ?? undefined}
                    label={series.sensor_name}
                    width={180}
                    height={180}
                    threshold={{ warning: 0.7, critical: 0.9 }}
                  />
                </div>
              ) : chartType === 'line' ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={sensorData} margin={{ top: 5, right: 30, left: 20, bottom: 25 }}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                    <XAxis
                      dataKey="timestamp"
                      tickFormatter={(timestamp: string) => {
                        const date = new Date(timestamp);
                        return interval === 'hour'
                          ? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                          : date.toLocaleDateString([], { month: 'short', day: 'numeric' });
                      }}
                      angle={-45}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis />
                    <ChartTooltip filteredSeries={[series]} />
                    <Line
                      type="monotone"
                      dataKey="value"
                      stroke={color}
                      strokeWidth={2}
                      dot={{ r: 1 }}
                      activeDot={{ r: 4 }}
                      connectNulls
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={sensorData} margin={{ top: 5, right: 30, left: 20, bottom: 25 }}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                    <XAxis
                      dataKey="timestamp"
                      tickFormatter={(timestamp: string) => {
                        const date = new Date(timestamp);
                        return interval === 'hour'
                          ? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                          : date.toLocaleDateString([], { month: 'short', day: 'numeric' });
                      }}
                      angle={-45}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis />
                    <ChartTooltip filteredSeries={[series]} />
                    <Area
                      type="monotone"
                      dataKey="value"
                      stroke={color}
                      fill={`${color}33`}
                      strokeWidth={2}
                      activeDot={{ r: 4 }}
                      connectNulls
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Sensor Stats */}
            <div className="grid grid-cols-3 gap-2 mt-4 text-center">
              <div className="bg-muted/50 p-2 rounded">
                <div className="text-xs text-muted-foreground">Min</div>
                <div className="font-medium">
                  {stats.minValue !== null && stats.minValue !== undefined ? stats.minValue.toFixed(1) : '-'}{' '}
                  {series.unit || ''}
                </div>
              </div>
              <div className="bg-muted/50 p-2 rounded">
                <div className="text-xs text-muted-foreground">Avg</div>
                <div className="font-medium">
                  {stats.avgValue !== null && stats.avgValue !== undefined ? stats.avgValue.toFixed(1) : '-'}{' '}
                  {series.unit || ''}
                </div>
              </div>
              <div className="bg-muted/50 p-2 rounded">
                <div className="text-xs text-muted-foreground">Max</div>
                <div className="font-medium">
                  {stats.maxValue !== null && stats.maxValue !== undefined ? stats.maxValue.toFixed(1) : '-'}{' '}
                  {series.unit || ''}
                </div>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// Add AI Insights Component with enhanced visualization
function AIInsightsCard({ deviceId, sensorData }: { deviceId: string; sensorData: any[] }) {
  const [insights, setInsights] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [anomalies, setAnomalies] = useState<any[]>([]);

  useEffect(() => {
    const generateInsights = async () => {
      if (!sensorData.length) return;

      setLoading(true);
      try {
        // Generate insights and detect anomalies
        const [aiInsights, detectedAnomalies] = await Promise.all([
          aiService.generateAdvancedInsights([{ id: deviceId }], sensorData),
          aiService.detectRealTimeAnomalies(sensorData, 24),
        ]);

        setInsights(aiInsights.slice(0, 3));
        setAnomalies(detectedAnomalies.slice(0, 5));
      } catch (error) {
        console.error('Failed to generate AI insights:', error);
      } finally {
        setLoading(false);
      }
    };

    generateInsights();
  }, [deviceId, sensorData]);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Brain className="h-5 w-5 text-purple-500" />
            AI Analytics Dashboard
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="animate-pulse text-muted-foreground">Analyzing sensor patterns and anomalies...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Brain className="h-5 w-5 text-purple-500" />
            AI Insights
            <Badge variant="secondary" className="ml-auto">
              <Sparkles className="h-3 w-3 mr-1" />
              Live Analysis
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {insights.length === 0 ? (
            <div className="text-center py-4 text-muted-foreground">
              <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No significant patterns detected</p>
            </div>
          ) : (
            insights.map((insight, index) => (
              <Alert key={index} className="border-l-4 border-l-blue-500">
                <TrendingUp className="h-4 w-4" />
                <AlertDescription>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">{insight.title}</span>
                      <Badge variant="outline" className="text-xs">
                        {insight.confidence}% confident
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">{insight.description}</p>
                    {insight.suggestedActions && (
                      <div className="mt-2">
                        <p className="text-xs font-medium mb-1">Recommended Actions:</p>
                        <ul className="text-xs space-y-1 text-muted-foreground">
                          {insight.suggestedActions.slice(0, 2).map((action: string, i: number) => (
                            <li key={i} className="flex items-center gap-1">
                              <div className="w-1 h-1 rounded-full bg-current" />
                              {action}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </AlertDescription>
              </Alert>
            ))
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            Anomaly Detection
            {anomalies.length > 0 && (
              <Badge variant="destructive" className="ml-auto">
                {anomalies.length} detected
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {anomalies.length === 0 ? (
            <div className="text-center py-4 text-muted-foreground">
              <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-500 opacity-50" />
              <p>No anomalies detected</p>
              <p className="text-xs">All sensors operating normally</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-48 overflow-y-auto">
              {anomalies.map((anomaly, index) => (
                <div key={index} className="flex items-center gap-3 p-3 border rounded-lg bg-muted/50">
                  <div
                    className={`w-2 h-2 rounded-full flex-shrink-0 ${
                      anomaly.severity === 'high'
                        ? 'bg-red-500'
                        : anomaly.severity === 'medium'
                          ? 'bg-yellow-500'
                          : 'bg-blue-500'
                    }`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium truncate">{anomaly.sensorType} Anomaly</span>
                      <Badge variant="outline" className="text-xs">
                        {anomaly.severity}
                      </Badge>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Value: {anomaly.value.toFixed(2)} (Expected: {anomaly.expectedRange.min.toFixed(1)}-
                      {anomaly.expectedRange.max.toFixed(1)})
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {anomaly.timestamp.toLocaleTimeString()} • Deviation: {anomaly.deviation.toFixed(2)}σ
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// --- Main AnalyticsTab ---
export function AnalyticsTab({ deviceId, timeRange, setTimeRange, timeRangeOptions }: AnalyticsTabProps) {
  const [chartType, setChartType] = useState<'line' | 'area' | 'gauge'>('line');
  const [selectedSensorType, setSelectedSensorType] = useState<string>('all');
  const [aggregation, setAggregation] = useState<string>('avg');
  const [interval, setInterval] = useState<string>(parseInt(timeRange) <= 24 ? 'hour' : 'day');

  useEffect(() => {
    const hours = parseInt(timeRange);
    if (hours <= 24) setInterval('hour');
    else if (hours <= 168) setInterval('day');
    else if (hours <= 720) setInterval('day');
    else setInterval('week');
  }, [timeRange]);

  const queryParams = useMemo(
    () => ({
      deviceIds: deviceId,
      sensorTypes: selectedSensorType !== 'all' ? selectedSensorType : undefined,
      startDate: new Date(Date.now() - parseInt(timeRange) * 60 * 60 * 1000).toISOString(),
      endDate: new Date().toISOString(),
      interval: interval as 'hour' | 'day' | 'week' | 'month',
      aggregation: aggregation as 'avg' | 'min' | 'max' | 'sum' | 'count',
    }),
    [deviceId, selectedSensorType, timeRange, interval, aggregation],
  );

  const {
    data: timeSeriesResponse,
    isLoading,
    isFetching,
    isError,
  } = useGetAnalyticsTimeSeriesQuery(queryParams, {
    refetchOnMountOrArgChange: true,
    pollingInterval: 0,
    skip: !deviceId,
  });

  const timeSeriesData: TimeSeries[] = useMemo(() => timeSeriesResponse?.data || [], [timeSeriesResponse]);

  const sensorTypes = useMemo(
    () => Array.from(new Set(timeSeriesData.map((series) => series.sensor_type))),
    [timeSeriesData],
  );

  const filteredSeries = useMemo(
    () =>
      selectedSensorType === 'all'
        ? timeSeriesData
        : timeSeriesData.filter((series) => series.sensor_type === selectedSensorType),
    [selectedSensorType, timeSeriesData],
  );

  const sensorsByUnit = useMemo(() => {
    const groupedSensors: Record<string, TimeSeries[]> = {};
    if (filteredSeries.length > 0) {
      filteredSeries.forEach((series) => {
        const unit = series.unit || 'unknown';
        if (!groupedSensors[unit]) groupedSensors[unit] = [];
        groupedSensors[unit].push(series);
      });
    }
    return groupedSensors;
  }, [filteredSeries]);

  // Convert time series data for AI analysis
  const aiSensorData = useMemo(() => {
    return filteredSeries.flatMap((series) =>
      (series.series || []).map((point) => ({
        timestamp: point.timestamp,
        value: point.value || 0,
        sensor_type: series.sensor_type,
        device_id: deviceId,
        unit: series.unit,
      })),
    );
  }, [filteredSeries, deviceId]);

  if (isError) {
    return (
      <div className="flex items-center justify-center h-[400px] text-destructive">
        Failed to load analytics data. Please try again later.
      </div>
    );
  }

  return (
    <div className="space-y-8 pt-4">
      <ChartControls
        deviceId={deviceId}
        selectedSensorType={selectedSensorType}
        setSelectedSensorType={setSelectedSensorType}
        sensorTypes={sensorTypes}
        interval={interval}
        setInterval={setInterval}
        aggregation={aggregation}
        setAggregation={setAggregation}
        chartType={chartType}
        setChartType={setChartType}
        timeRange={timeRange}
        setTimeRange={setTimeRange}
        timeRangeOptions={timeRangeOptions}
      />

      <Card>
        <CardHeader>
          <CardTitle className="capitalize">
            {selectedSensorType !== 'all' ? `${selectedSensorType} Trends` : 'Sensor Trends'}
            <span className="text-sm font-normal text-muted-foreground ml-2">
              ({AGGREGATION_OPTIONS.find((o) => o.value === aggregation)?.label || 'Average'} •{' '}
              {INTERVAL_OPTIONS.find((o) => o.value === interval)?.label || 'Hourly'})
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading || isFetching ? (
            <Skeleton className="h-[400px] w-full rounded-md" />
          ) : filteredSeries.length === 0 ? (
            <div className="flex items-center justify-center h-[400px] text-muted-foreground">
              No data available for the selected filters.
            </div>
          ) : chartType === 'gauge' ? (
            <GaugeChartGrid series={filteredSeries} />
          ) : (
            <GroupedCharts
              chartType={chartType}
              sensorsByUnit={sensorsByUnit}
              filteredSeries={filteredSeries}
              interval={interval}
            />
          )}
        </CardContent>
      </Card>

      {/* Enhanced AI Insights Cards */}
      {!isLoading && !isFetching && filteredSeries.length > 0 && (
        <AIInsightsCard deviceId={deviceId} sensorData={aiSensorData} />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredSeries.map((series, index) => (
          <SensorCard
            key={series.sensor_id}
            series={series}
            color={CHART_COLORS[index % CHART_COLORS.length]}
            chartType={chartType}
            interval={interval}
            isLoading={isLoading || isFetching}
          />
        ))}
      </div>
    </div>
  );
}
